import { createContext, useContext, useState, useEffect } from 'react';
import { ref, onValue, update, off } from 'firebase/database';
import { db } from '../configs/Firebase';

const NotificationsContext = createContext();

export const NotificationsProvider = ({ children, userId }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const notificationsRef = ref(db, `userData/${userId}/notifications`);

    const unsubscribe = onValue(notificationsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const notificationsList = [];
        for (let i in data) {
          notificationsList.push({
            id: i,
            ...data[i],
          });
        }
        // Sort by timestamp (most recent first)
        notificationsList.sort((a, b) => {
          return (b.timestamp || 0) - (a.timestamp || 0);
        });
        setNotifications(notificationsList);
        
        // Calculate unread count
        const unread = notificationsList.filter(n => !n.read).length;
        setUnreadCount(unread);
      } else {
        setNotifications([]);
        setUnreadCount(0);
      }
      setLoading(false);
    });

    return () => {
      off(notificationsRef);
      unsubscribe();
    };
  }, [userId]);

  const markAsRead = async (notificationId) => {
    if (!userId || !notificationId) return;
    
    const notificationRef = ref(db, `userData/${userId}/notifications/${notificationId}`);
    await update(notificationRef, { read: true });
  };

  const markAllAsRead = async () => {
    if (!userId) return;
    
    const updates = {};
    notifications.forEach(notification => {
      if (!notification.read) {
        updates[`userData/${userId}/notifications/${notification.id}/read`] = true;
      }
    });
    
    if (Object.keys(updates).length > 0) {
      await update(ref(db), updates);
    }
  };

  const markCellNotificationsAsRead = async (journeyId, rowId, cellId) => {
    if (!userId) return;
    
    const updates = {};
    notifications.forEach(notification => {
      if (
        !notification.read &&
        notification.journeyId === journeyId &&
        notification.rowId === rowId &&
        notification.cellId === cellId
      ) {
        updates[`userData/${userId}/notifications/${notification.id}/read`] = true;
      }
    });
    
    if (Object.keys(updates).length > 0) {
      await update(ref(db), updates);
    }
  };

  const getUnreadCountForCell = (journeyId, rowId, cellId) => {
    return notifications.filter(
      n => !n.read &&
      n.journeyId === journeyId &&
      n.rowId === rowId &&
      n.cellId === cellId
    ).length;
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    markCellNotificationsAsRead,
    getUnreadCountForCell,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    // Return default values instead of throwing to allow graceful degradation
    return {
      notifications: [],
      unreadCount: 0,
      loading: false,
      markAsRead: async () => {},
      markAllAsRead: async () => {},
      markCellNotificationsAsRead: async () => {},
      getUnreadCountForCell: () => 0,
    };
  }
  return context;
};

